/**
 * API Configuration
 *
 * Update the API_URL to point to your deployed backend API.
 *
 * For production, use your deployed Cloud Run URL or custom domain.
 * For development, you can use localhost if running the API locally.
 */

export const API_CONFIG = {
  // Replace with your actual deployed API URL
  API_URL: import.meta.env.VITE_API_URL || 'https://meshly-validate-gateway-bs3uuqmx.uc.gateway.dev',

  // Example URLs:
  // API_URL: 'https://meshly-api-1234567890.us-central1.run.app',
  // API_URL: 'https://api.meshly.ai',
  // API_URL: 'http://localhost:8080', // For local development
};

/**
 * Helper function to get the full API URL for a given endpoint.
 * Used for non-subscribe endpoints (e.g. HEALTH). For form submissions,
 * use getSubscribeUrl() instead — it applies the runtime hostname switch.
 */
export const getApiUrl = (endpoint: string): string => {
  const baseUrl = API_CONFIG.API_URL.replace(/\/$/, ''); // Remove trailing slash
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`;
};

/**
 * Subscription API endpoints
 */
export const SUBSCRIPTION_ENDPOINTS = {
  SUBSCRIBE: '/api/subscribe',
  HEALTH: '/api/subscribe/health',
} as const;

// Hostnames that stay on the legacy validate-gateway backend.
// ONE-LINE cutover: clear this set once www is ready to move to forms.meshly.ai.
const LEGACY_FORMS_HOSTS = new Set(['www.meshly.ai', 'meshly.ai']);

// Form `list` values the new forms catcher (forms.meshly.ai) does NOT accept.
// The catcher is scoped to marketing lead lists and deliberately rejects
// product-signup lists (MES-1010), returning "that signup form isn't recognized"
// (MES-1169). These must always POST to the legacy validate-gateway
// (/api/subscribe -> meshly-backend, which handles them), regardless of host.
const CATCHER_UNSUPPORTED_LISTS = new Set(['meshly-validate']);

/**
 * Runtime forms base URL — resolved at call time so the same Firebase build
 * artifact serves both www.meshly.ai (legacy) and beta.meshly.ai (new catcher).
 *
 * `list` lets a form opt out of the catcher: any list the catcher rejects
 * (CATCHER_UNSUPPORTED_LISTS) stays on the legacy backend on every host.
 *
 * SSR-safe: falls back to the legacy endpoint when window is unavailable.
 */
function getFormsBaseUrl(list?: string): string {
  const legacy = API_CONFIG.API_URL.replace(/\/$/, '');
  // Lists the catcher rejects never go to it — keep them on the legacy backend.
  if (list && CATCHER_UNSUPPORTED_LISTS.has(list)) {
    return legacy;
  }
  if (typeof window === 'undefined') {
    // SSR / pre-render: default to legacy (www behaviour is unchanged).
    return legacy;
  }
  return LEGACY_FORMS_HOSTS.has(window.location.hostname)
    ? legacy
    : 'https://forms.meshly.ai';
}

/**
 * Full subscribe endpoint URL, resolved at call time.
 * Use this in every form submission — it handles the beta vs www routing.
 * Pass the form's `list` so lists the catcher rejects stay on the legacy backend.
 */
export const getSubscribeUrl = (list?: string): string =>
  `${getFormsBaseUrl(list)}${SUBSCRIPTION_ENDPOINTS.SUBSCRIBE}`;
